import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  CalendarPlus,
  CalendarSearch,
  Plus,
  DollarSign,
  Gauge,
  CalendarCheck,
  Bell,
  ChevronRight,
  CreditCard,
  RefreshCw,
  Ban,
  ChevronLeft,
} from "lucide-react";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Money } from "@/components/ui/riyal";
import { useDirection } from "@/hooks/use-direction";

export const Route = createFileRoute("/merchant/fast-action")({
  head: () => ({
    meta: [
      { title: "Fast Action — Spayzo Merchant" },
      {
        name: "description",
        content:
          "Accept bookings, create manual reservations, and check availability in a single click.",
      },
    ],
  }),
  component: FastActionPage,
});

/* -------------------------------------------------------------------------- */
/* Data                                                                       */
/* -------------------------------------------------------------------------- */

type Status = "upcoming" | "pending" | "completed";

const SPACE_KEYS = [
  "gardenStudio",
  "rooftopLounge",
  "meetingOak",
  "podcastBooth",
  "coworkingFloor",
] as const;

type Pending = {
  id: string;
  customer: string;
  initials: string;
  spaceKey: (typeof SPACE_KEYS)[number];
  dateKey: string;
  time: string;
  guests: number;
  total: React.ReactNode;
};

const initialPending: Pending[] = [
  {
    id: "BK-2041",
    customer: "Lina Park",
    initials: "LP",
    spaceKey: "gardenStudio",
    dateKey: "today",
    time: "14:00 – 17:00",
    guests: 8,
    total: <Money value="184" />,
  },
  {
    id: "BK-2042",
    customer: "Marcus Diallo",
    initials: "MD",
    spaceKey: "rooftopLounge",
    dateKey: "tomorrow",
    time: "19:00 – 23:00",
    guests: 24,
    total: <Money value="612" />,
  },
  {
    id: "BK-2043",
    customer: "Sofia Reyes",
    initials: "SR",
    spaceKey: "meetingOak",
    dateKey: "may26",
    time: "09:30 – 11:30",
    guests: 6,
    total: <Money value="96" />,
  },
];

const upcoming = [
  {
    id: "BK-2031",
    customer: "Hana Schmidt",
    initials: "HS",
    spaceKey: "rooftopLounge" as const,
    dateKey: "todayAt",
    status: "upcoming" as Status,
  },
  {
    id: "BK-2032",
    customer: "Owen Tate",
    initials: "OT",
    spaceKey: "podcastBooth" as const,
    dateKey: "tomorrowAt",
    status: "upcoming" as Status,
  },
];

const completed = [
  {
    id: "BK-2024",
    customer: "Aiko Tanaka",
    initials: "AT",
    spaceKey: "gardenStudio" as const,
    dateKey: "yesterdayAt15",
    status: "completed" as Status,
  },
  {
    id: "BK-2023",
    customer: "Ravi Mehta",
    initials: "RM",
    spaceKey: "meetingOak" as const,
    dateKey: "yesterdayAt09",
    status: "completed" as Status,
  },
];

type Row = {
  id: string;
  customer: string;
  initials: string;
  spaceKey: (typeof SPACE_KEYS)[number];
  dateKey: string;
  extra?: string;
  status: Status;
};

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

function FastActionPage() {
  const { t } = useTranslation();
  const [pending] = useState<Pending[]>(initialPending);
  const [manualOpen, setManualOpen] = useState(false);
  const [availOpen, setAvailOpen] = useState(false);

  const kpis = [
    {
      label: t("pages.merchant.fastAction.kpis.today"),
      value: "14" as React.ReactNode,
      delta: t("pages.merchant.fastAction.kpis.todayDelta"),
      icon: CalendarCheck,
      accent: "from-primary/15 to-primary/0",
    },
    {
      label: t("pages.merchant.fastAction.kpis.revenue"),
      value: <Money value="28,940" />,
      delta: t("pages.merchant.fastAction.kpis.revenueDelta"),
      icon: DollarSign,
      accent: "from-emerald-500/15 to-emerald-500/0",
    },
    {
      label: t("pages.merchant.fastAction.kpis.occupancy"),
      value: "78%",
      delta: t("pages.merchant.fastAction.kpis.occupancyDelta"),
      icon: Gauge,
      accent: "from-amber-500/15 to-amber-500/0",
    },
    {
      label: t("pages.merchant.fastAction.kpis.pending"),
      value: "3",
      delta: t("pages.merchant.fastAction.kpis.pendingDelta"),
      icon: Bell,
      accent: "from-rose-500/15 to-rose-500/0",
    },
  ];

  return (
    <MerchantShell
      title={t("merchantFastAction.title")}
      subtitle={t("merchantFastAction.subtitle")}
      actions={
        <>
          <Button variant="outline" className="h-10 rounded-xl" onClick={() => setAvailOpen(true)}>
            <CalendarSearch className="h-4 w-4" /> {t("merchantFastAction.checkAvailability")}
          </Button>
          <Button className="h-10 rounded-xl shadow-soft" onClick={() => setManualOpen(true)}>
            <Plus className="h-4 w-4" /> {t("merchantFastAction.manualBooking")}
          </Button>
        </>
      }
    >
      {/* Quick actions hero */}
      <section className="grid gap-4 lg:grid-cols-2">
        <QuickActionCard
          tone="primary"
          icon={CalendarPlus}
          eyebrow={t("pages.merchant.fastAction.quick.createEyebrow")}
          title={t("pages.merchant.fastAction.quick.createTitle")}
          description={t("pages.merchant.fastAction.quick.createDesc")}
          cta={t("pages.merchant.fastAction.quick.createCta")}
          onClick={() => setManualOpen(true)}
        />
        <QuickActionCard
          tone="emerald"
          icon={CalendarSearch}
          eyebrow={t("pages.merchant.fastAction.quick.lookupEyebrow")}
          title={t("pages.merchant.fastAction.quick.lookupTitle")}
          description={t("pages.merchant.fastAction.quick.lookupDesc")}
          cta={t("pages.merchant.fastAction.quick.lookupCta")}
          onClick={() => setAvailOpen(true)}
        />
      </section>

      {/* KPI strip */}
      <section className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k) => (
          <Card
            key={k.label}
            className="relative overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft"
          >
            <div className={cn("absolute inset-0 -z-0 bg-gradient-to-br", k.accent)} />
            <div className="relative flex items-start justify-between">
              <div className="text-start">
                <p className="text-xs font-medium text-muted-foreground">{k.label}</p>
                <p className="mt-2 font-display text-2xl font-bold tracking-tight">{k.value}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{k.delta}</p>
              </div>
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-background/80 text-primary shadow-soft ring-1 ring-border/60">
                <k.icon className="h-5 w-5" />
              </span>
            </div>
          </Card>
        ))}
      </section>

      {/* Main grid */}
      <section className="mt-6 grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2 space-y-6">
          <BookingsOverview upcoming={upcoming} pending={pending} completed={completed} />
          <AvailabilityCalendar />
        </div>

        <div className="space-y-6">
          <NotificationsPanel />
          <ActivityFeed />
        </div>
      </section>

      {/* Sticky mobile quick bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border/60 bg-background/95 px-3 py-2.5 backdrop-blur xl:hidden">
        <div className="mx-auto flex max-w-7xl items-center gap-2">
          <Button variant="outline" className="h-11 flex-1 rounded-xl" onClick={() => setAvailOpen(true)}>
            <CalendarSearch className="h-4 w-4" /> {t("pages.merchant.fastAction.mobile.availability")}
          </Button>
          <Button className="h-11 flex-1 rounded-xl shadow-soft" onClick={() => setManualOpen(true)}>
            <Plus className="h-4 w-4" /> {t("pages.merchant.fastAction.mobile.booking")}
          </Button>
        </div>
      </div>
      <div className="h-16 xl:hidden" aria-hidden />

      <ManualBookingDialog open={manualOpen} onOpenChange={setManualOpen} />
      <AvailabilityDialog open={availOpen} onOpenChange={setAvailOpen} />
    </MerchantShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Quick action cards                                                         */
/* -------------------------------------------------------------------------- */

function QuickActionCard({
  tone,
  icon: Icon,
  eyebrow,
  title,
  description,
  cta,
  onClick,
}: {
  tone: "primary" | "emerald";
  icon: typeof CalendarPlus;
  eyebrow: string;
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
}) {
  const dir = useDirection();
  const Chevron = dir === "rtl" ? ChevronLeft : ChevronRight;
  const grad =
    tone === "primary"
      ? "from-primary/10 via-transparent to-transparent"
      : "from-emerald-500/10 via-transparent to-transparent";
  const dot = tone === "primary" ? "bg-primary text-primary-foreground" : "bg-emerald-600 text-white";
  const eyebrowColor = tone === "primary" ? "text-primary" : "text-emerald-700";
  return (
    <Card className="group relative flex flex-col overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft transition-shadow hover:shadow-md">
      <div className={cn("absolute inset-0 -z-0 bg-gradient-to-br", grad)} />
      <div className="relative flex items-center gap-2">
        <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl shadow-soft", dot)}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="text-start">
          <p className={cn("text-[11px] font-semibold uppercase tracking-wider", eyebrowColor)}>
            {eyebrow}
          </p>
          <p className="font-display text-base font-bold">{title}</p>
        </div>
      </div>
      <p className="relative mt-4 text-start text-sm text-muted-foreground">{description}</p>
      <Button
        variant="ghost"
        className="relative mt-auto h-10 justify-between rounded-xl px-3 group-hover:bg-background/80"
        onClick={onClick}
      >
        <span className="font-semibold">{cta}</span>
        <Chevron className="h-4 w-4" />
      </Button>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Bookings overview                                                          */
/* -------------------------------------------------------------------------- */

const statusStyles: Record<Status, string> = {
  upcoming: "bg-primary/10 text-primary",
  pending: "bg-amber-500/15 text-amber-700",
  completed: "bg-emerald-500/15 text-emerald-700",
};

function BookingsOverview({
  upcoming,
  pending,
  completed,
}: {
  upcoming: Row[];
  pending: Pending[];
  completed: Row[];
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Status>("upcoming");
  const rows: Row[] =
    tab === "upcoming"
      ? upcoming
      : tab === "pending"
        ? pending.map((p) => ({
            id: p.id,
            customer: p.customer,
            initials: p.initials,
            spaceKey: p.spaceKey,
            dateKey: p.dateKey,
            extra: p.time,
            status: "pending" as Status,
          }))
        : completed;

  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-start">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {t("pages.merchant.fastAction.overview.title")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t("pages.merchant.fastAction.overview.subtitle")}
          </p>
        </div>
        <div className="inline-flex rounded-xl bg-surface p-1 ring-1 ring-border/60">
          {(["upcoming", "pending", "completed"] as Status[]).map((s) => (
            <button
              key={s}
              onClick={() => setTab(s)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                tab === s ? "bg-background shadow-soft" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`pages.merchant.fastAction.overview.${s}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 divide-y divide-border/60">
        {rows.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t("pages.merchant.fastAction.overview.empty")}
          </p>
        )}
        {rows.map((b) => (
          <div key={b.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                {b.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 text-start">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-semibold">{b.customer}</p>
                <Badge className={cn("rounded-full border-transparent text-[10px]", statusStyles[b.status])}>
                  {t(`pages.merchant.fastAction.overview.${b.status}`)}
                </Badge>
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {t(`pages.merchant.fastAction.spaces.${b.spaceKey}`)} ·{" "}
                {t(`pages.merchant.fastAction.dates.${b.dateKey}`)}
                {b.extra ? ` · ${b.extra}` : ""}
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-1.5">
              <Button size="sm" variant="ghost" className="h-8 rounded-lg px-2 text-xs">
                {t("pages.merchant.fastAction.overview.details")}
              </Button>
              {b.status !== "completed" && (
                <Button size="sm" className="h-8 rounded-lg px-3 text-xs shadow-soft">
                  {t("pages.merchant.fastAction.overview.manage")}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Availability calendar                                                      */
/* -------------------------------------------------------------------------- */

function AvailabilityCalendar() {
  const { t, i18n } = useTranslation();
  const dir = useDirection();
  const isAr = i18n.language === "ar";
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const days = useMemo(() => buildMonth(cursor), [cursor]);
  const monthLabel = cursor.toLocaleString(isAr ? "ar" : "en", { month: "long", year: "numeric" });
  const dows = isAr
    ? ["ح", "ن", "ث", "ر", "خ", "ج", "س"]
    : ["S", "M", "T", "W", "T", "F", "S"];

  const Prev = dir === "rtl" ? ChevronRight : ChevronLeft;
  const Next = dir === "rtl" ? ChevronLeft : ChevronRight;

  const stateFor = (d: Date | null): "available" | "booked" | "blocked" | null => {
    if (!d) return null;
    const key = d.getDate() + d.getMonth();
    const m = key % 7;
    if (m === 0 || m === 3) return "booked";
    if (m === 5) return "blocked";
    return "available";
  };

  const legend = [
    {
      label: t("pages.merchant.fastAction.calendar.available"),
      className: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30",
    },
    {
      label: t("pages.merchant.fastAction.calendar.booked"),
      className: "bg-primary/15 text-primary ring-primary/30",
    },
    {
      label: t("pages.merchant.fastAction.calendar.blocked"),
      className: "bg-muted text-muted-foreground ring-border",
    },
  ];

  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-start">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {t("pages.merchant.fastAction.calendar.title")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t("pages.merchant.fastAction.calendar.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            className="h-9 w-9 rounded-lg"
            aria-label={t("pages.merchant.fastAction.calendar.prevMonth", "Previous month")}
            onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
          >
            <Prev className="h-4 w-4" />
          </Button>
          <span className="min-w-[140px] text-center text-sm font-semibold">{monthLabel}</span>
          <Button
            size="icon"
            variant="outline"
            className="h-9 w-9 rounded-lg"
            aria-label={t("pages.merchant.fastAction.calendar.nextMonth", "Next month")}
            onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
          >
            <Next className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1.5 text-center text-[11px] font-medium text-muted-foreground">
        {dows.map((d, i) => (
          <div key={i} className="py-1">
            {d}
          </div>
        ))}
        {days.map((d, i) => {
          const s = stateFor(d);
          const isToday =
            d &&
            d.getDate() === today.getDate() &&
            d.getMonth() === today.getMonth() &&
            d.getFullYear() === today.getFullYear();
          if (!d) return <div key={i} className="aspect-square" />;
          return (
            <button
              key={i}
              aria-label={d.toDateString()}
              className={cn(
                "aspect-square rounded-lg text-xs font-medium transition-colors ring-1 ring-inset",
                !d && "ring-transparent",
                s === "available" && "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 hover:bg-emerald-500/20",
                s === "booked" && "bg-primary/10 text-primary ring-primary/20 hover:bg-primary/20",
                s === "blocked" && "bg-muted text-muted-foreground ring-border",
                isToday && "outline outline-2 outline-primary",
              )}
            >
              {d?.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {legend.map((l) => (
            <span
              key={l.label}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1",
                l.className,
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" /> {l.label}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-9 rounded-lg">
            <Ban className="h-4 w-4" /> {t("pages.merchant.fastAction.calendar.blockTime")}
          </Button>
          <Button size="sm" className="h-9 rounded-lg shadow-soft">
            <Plus className="h-4 w-4" /> {t("pages.merchant.fastAction.calendar.openSlots")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function buildMonth(cursor: Date): (Date | null)[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const lead = first.getDay();
  const cells: (Date | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= last.getDate(); d++) {
    cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/* -------------------------------------------------------------------------- */
/* Activity + Notifications                                                   */
/* -------------------------------------------------------------------------- */

function ActivityFeed() {
  const { t } = useTranslation();
  const activity = [
    {
      icon: CalendarPlus,
      color: "text-primary bg-primary/10",
      text: t("pages.merchant.fastAction.activity.newBooking"),
      time: t("pages.merchant.fastAction.activity.min2"),
    },
    {
      icon: CreditCard,
      color: "text-emerald-600 bg-emerald-500/10",
      text: t("pages.merchant.fastAction.activity.payment"),
      time: t("pages.merchant.fastAction.activity.min18"),
    },
    {
      icon: Ban,
      color: "text-rose-600 bg-rose-500/10",
      text: t("pages.merchant.fastAction.activity.cancelled"),
      time: t("pages.merchant.fastAction.activity.hour1"),
    },
    {
      icon: RefreshCw,
      color: "text-amber-600 bg-amber-500/10",
      text: t("pages.merchant.fastAction.activity.renewed"),
      time: t("pages.merchant.fastAction.activity.hour3"),
    },
    {
      icon: CheckCircle2,
      color: "text-emerald-600 bg-emerald-500/10",
      text: t("pages.merchant.fastAction.activity.completed"),
      time: t("pages.merchant.fastAction.activity.yesterday"),
    },
  ];

  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold tracking-tight">
          {t("pages.merchant.fastAction.activity.title")}
        </h2>
        <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs">
          {t("pages.merchant.fastAction.activity.viewAll")}
        </Button>
      </div>
      <ul className="mt-3 space-y-3">
        {activity.map((a, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", a.color)}>
              <a.icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1 text-start">
              <p className="text-sm">{a.text}</p>
              <p className="text-[11px] text-muted-foreground">{a.time}</p>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function NotificationsPanel() {
  const { t } = useTranslation();
  const toneMap = {
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-500/15 text-emerald-700",
    amber: "bg-amber-500/15 text-amber-700",
  };
  const notifications = [
    {
      title: t("pages.merchant.fastAction.notifications.n1Title"),
      description: t("pages.merchant.fastAction.notifications.n1Desc"),
      tag: t("pages.merchant.fastAction.notifications.n1Tag"),
      tone: "primary" as const,
    },
    {
      title: t("pages.merchant.fastAction.notifications.n2Title"),
      description: t("pages.merchant.fastAction.notifications.n2Desc"),
      tag: t("pages.merchant.fastAction.notifications.n2Tag"),
      tone: "emerald" as const,
    },
    {
      title: t("pages.merchant.fastAction.notifications.n3Title"),
      description: t("pages.merchant.fastAction.notifications.n3Desc"),
      tag: t("pages.merchant.fastAction.notifications.n3Tag"),
      tone: "amber" as const,
    },
  ];

  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
      <div className="flex items-center justify-between">
        <div className="text-start">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {t("pages.merchant.fastAction.notifications.title")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t("pages.merchant.fastAction.notifications.subtitle")}
          </p>
        </div>
        <Badge className="rounded-full bg-primary text-primary-foreground">
          {notifications.length}
        </Badge>
      </div>
      <ul className="mt-3 space-y-2.5">
        {notifications.map((n, i) => (
          <li
            key={i}
            className="rounded-xl border border-border/60 bg-surface/40 p-3 transition-colors hover:bg-surface"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-start text-sm font-semibold">{n.title}</p>
              <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium", toneMap[n.tone])}>
                {n.tag}
              </span>
            </div>
            <p className="mt-0.5 text-start text-xs text-muted-foreground">{n.description}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Dialogs                                                                    */
/* -------------------------------------------------------------------------- */

function ManualBookingDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const dir = useDirection();
  const [form, setForm] = useState({
    spaceKey: SPACE_KEYS[0] as (typeof SPACE_KEYS)[number],
    date: "",
    time: "",
    customer: "",
    email: "",
    addons: "",
  });

  const submit = () => {
    if (!form.customer || !form.date || !form.time) {
      toast.error(t("pages.merchant.fastAction.manual.error"));
      return;
    }
    toast.success(t("pages.merchant.fastAction.manual.success"), {
      description: `${form.customer} · ${t(`pages.merchant.fastAction.spaces.${form.spaceKey}`)} · ${form.date} ${form.time}`,
    });
    onOpenChange(false);
    setForm({ spaceKey: SPACE_KEYS[0], date: "", time: "", customer: "", email: "", addons: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} className="rounded-2xl text-start sm:max-w-lg">
        <DialogHeader className="text-start">
          <DialogTitle className="font-display text-xl">
            {t("pages.merchant.fastAction.manual.title")}
          </DialogTitle>
          <DialogDescription>{t("pages.merchant.fastAction.manual.desc")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>{t("pages.merchant.fastAction.manual.space")}</Label>
            <Select
              value={form.spaceKey}
              onValueChange={(v) => setForm({ ...form, spaceKey: v as (typeof SPACE_KEYS)[number] })}
            >
              <SelectTrigger className="h-10 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPACE_KEYS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`pages.merchant.fastAction.spaces.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>{t("pages.merchant.fastAction.manual.date")}</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="h-10 rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label>{t("pages.merchant.fastAction.manual.time")}</Label>
              <Input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="h-10 rounded-xl"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>{t("pages.merchant.fastAction.manual.customer")}</Label>
              <Input
                placeholder={t("pages.merchant.fastAction.manual.customerPh")}
                value={form.customer}
                onChange={(e) => setForm({ ...form, customer: e.target.value })}
                className="h-10 rounded-xl text-start"
              />
            </div>
            <div className="grid gap-2">
              <Label>{t("pages.merchant.fastAction.manual.contact")}</Label>
              <Input
                placeholder={t("pages.merchant.fastAction.manual.contactPh")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-10 rounded-xl text-start"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>{t("pages.merchant.fastAction.manual.addons")}</Label>
            <Textarea
              placeholder={t("pages.merchant.fastAction.manual.addonsPh")}
              value={form.addons}
              onChange={(e) => setForm({ ...form, addons: e.target.value })}
              className="min-h-[72px] rounded-xl text-start"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-10 rounded-xl" onClick={() => onOpenChange(false)}>
            {t("pages.merchant.fastAction.manual.cancel")}
          </Button>
          <Button className="h-10 rounded-xl shadow-soft" onClick={submit}>
            <CheckCircle2 className="h-4 w-4" /> {t("pages.merchant.fastAction.manual.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AvailabilityDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const dir = useDirection();
  const [spaceKey, setSpaceKey] = useState<(typeof SPACE_KEYS)[number]>(SPACE_KEYS[0]);
  const [date, setDate] = useState("");

  const slots = useMemo(() => {
    const base = ["09:00", "10:30", "12:00", "13:30", "15:00", "16:30", "18:00", "19:30"];
    const seed = (date + spaceKey).length;
    return base.map((time, i) => ({ time, taken: (i + seed) % 3 === 0 }));
  }, [date, spaceKey]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} className="rounded-2xl text-start sm:max-w-lg">
        <DialogHeader className="text-start">
          <DialogTitle className="font-display text-xl">
            {t("pages.merchant.fastAction.avail.title")}
          </DialogTitle>
          <DialogDescription>{t("pages.merchant.fastAction.avail.desc")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label>{t("pages.merchant.fastAction.avail.space")}</Label>
            <Select
              value={spaceKey}
              onValueChange={(v) => setSpaceKey(v as (typeof SPACE_KEYS)[number])}
            >
              <SelectTrigger className="h-10 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPACE_KEYS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`pages.merchant.fastAction.spaces.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>{t("pages.merchant.fastAction.avail.date")}</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 rounded-xl"
            />
          </div>

          <div className="mt-2 grid grid-cols-4 gap-2">
            {slots.map((s) => (
              <button
                key={s.time}
                disabled={s.taken}
                onClick={() =>
                  toast.success(t("pages.merchant.fastAction.avail.held", { time: s.time }), {
                    description: t(`pages.merchant.fastAction.spaces.${spaceKey}`),
                  })
                }
                className={cn(
                  "rounded-xl px-2 py-2.5 text-sm font-medium ring-1 ring-inset transition-colors",
                  s.taken
                    ? "cursor-not-allowed bg-muted text-muted-foreground ring-border line-through"
                    : "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30 hover:bg-emerald-500/20",
                )}
              >
                {s.time}
              </button>
            ))}
          </div>
          <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />{" "}
              {t("pages.merchant.fastAction.avail.available")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-muted-foreground/60" />{" "}
              {t("pages.merchant.fastAction.avail.occupied")}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="h-10 rounded-xl" onClick={() => onOpenChange(false)}>
            {t("pages.merchant.fastAction.avail.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Optional dialog trigger export helper (unused, prevents tree-shake warnings) */
export const _unused = DialogTrigger;
